// FUNCIÓN ÚNICA de cálculo del total: vive en un módulo puro compartido con las
// edge functions. Ningún archivo del frontend debe volver a sumar precios a mano.
export {
  calculateReservationTotal,
  assertReservationTotalInvariant,
} from "../../supabase/functions/_shared/reservation-total";
export type {
  ReservationTotalParts,
  ReservationTotalBreakdown,
} from "../../supabase/functions/_shared/reservation-total";

/**
 * Total real de una reserva (alojamiento + servicios adicionales).
 *
 * PROBLEMA HISTÓRICO:
 * - Reservas WEB (`create-payment`): `reservations.total` YA incluye los servicios
 *   adicionales (decoración) además del subtotal y la tarifa de servicio.
 * - Reservas MANUALES antiguas (`CreateReservationDialog`): `total` = `subtotal`
 *   y la decoración quedaba SOLO en `reservation_additional_services`.
 *
 * Sumar siempre los servicios inflaba el total de las web (doble conteo) y no
 * sumarlos nunca dejaba de cobrar la decoración en las manuales.
 *
 * Regla determinista: los servicios ya están incluidos cuando
 * `total >= subtotal + serviceFee + servicesTotal`.
 */

export type ReservationTotalInput = {
  total: number;
  subtotal?: number | null;
  serviceFee?: number | null;
  servicesTotal: number;
  /**
   * Bandera explícita `reservations.total_incluye_servicios`.
   * - true  → el total YA contiene los servicios
   * - false → el total NO los contiene (hay que sumarlos)
   * - null/undefined → sin bandera: se usa la heurística legacy y se marca
   *   `needsReview` para que la UI pida confirmación antes de autocobrar.
   */
  totalIncluyeServicios?: boolean | null;
};

export type ReservationTotalResult = {
  /** true si `reservations.total` ya contiene los servicios adicionales */
  servicesIncluded: boolean;
  /** Total real a cobrar al huésped por la reserva */
  grandTotal: number;
  /** Porción de alojamiento (sin servicios adicionales) */
  lodgingTotal: number;
  /** true si no hay bandera explícita y se resolvió por heurística */
  needsReview: boolean;
};

export function computeReservationGrandTotal(
  input: ReservationTotalInput
): ReservationTotalResult {
  const total = input.total || 0;
  const subtotal = input.subtotal ?? 0;
  const serviceFee = input.serviceFee ?? 0;
  const servicesTotal = input.servicesTotal || 0;
  const flag = input.totalIncluyeServicios;

  if (servicesTotal <= 0) {
    return { servicesIncluded: true, grandTotal: total, lodgingTotal: total, needsReview: false };
  }

  // 1) Bandera explícita: es la fuente de verdad.
  if (flag === true || flag === false) {
    const grandTotal = flag ? total : total + servicesTotal;
    return {
      servicesIncluded: flag,
      grandTotal,
      lodgingTotal: Math.max(0, grandTotal - servicesTotal),
      needsReview: false,
    };
  }

  // 2) Sin bandera: heurística legacy + needsReview.
  const servicesIncluded = total >= subtotal + serviceFee + servicesTotal;
  const grandTotal = servicesIncluded ? total : total + servicesTotal;

  return {
    servicesIncluded,
    grandTotal,
    lodgingTotal: Math.max(0, grandTotal - servicesTotal),
    needsReview: true,
  };
}

/**
 * Dado un nuevo total general editado por admin, devuelve el valor que debe
 * guardarse en `reservations.total` respetando la convención de la reserva.
 */
export function toStoredReservationTotal(
  newGrandTotal: number,
  servicesTotal: number,
  servicesIncluded: boolean
): number {
  return servicesIncluded
    ? Math.max(0, newGrandTotal)
    : Math.max(0, newGrandTotal - (servicesTotal || 0));
}

/* ------------------------------------------------------------------ *
 * Saldo de tarifa (hospedaje) — fórmula ramificada con piso
 * ------------------------------------------------------------------ */

export type ReservationBalanceInput = {
  /** Total real ya resuelto por `computeReservationGrandTotal` */
  grandTotal: number;
  /** `reservations.deposit_amount` (0 si no hay anticipo) */
  depositAmount: number;
  /**
   * Suma de `income_transactions` con income_type IN ('hotel_deposit','hotel_balance').
   * Solo el eje de TARIFA: consumos, propinas y extras quedan fuera.
   */
  tariffPayments: number;
  /** `checked_in_at != null || status in ('completed','no_show')` */
  postCheckIn: boolean;
  /** `check_out < hoy` (Colombia). Habilita el piso histórico extendido. */
  checkOutPast?: boolean;
  /** `reservations.deposit_paid` — prueba de que hubo un pago real. */
  depositPaid?: boolean | null;
};

export type ReservationBalanceStatus =
  | "saldado"
  | "saldado_historico"
  | "pendiente"
  | "no_cobrado_al_cierre"
  | "revisar";

export type ReservationBalanceResult = {
  /** Pagos reconocidos contra la tarifa */
  paidAmount: number;
  /** grandTotal − paidAmount (puede ser negativo) */
  balance: number;
  status: ReservationBalanceStatus;
  label: string;
};

const BALANCE_LABELS: Record<ReservationBalanceStatus, string> = {
  saldado: "Saldado",
  saldado_historico: "Saldado — contabilidad no migrada",
  pendiente: "Saldo pendiente",
  no_cobrado_al_cierre: "No cobrado al cierre",
  revisar: "Revisar / diferencia a favor por confirmar",
};

export function computeReservationBalance(
  input: ReservationBalanceInput
): ReservationBalanceResult {
  const grandTotal = input.grandTotal || 0;
  const depositAmount = input.depositAmount || 0;
  const tariffPayments = input.tariffPayments || 0;

  // Piso histórico extendido: la estadía ya terminó, hubo anticipo pagado
  // (prueba de pago real) y la contabilidad nunca se migró como transacción.
  // Mismo principio que el GREATEST, aplicado también a pre-check-in histórico.
  const historicalSettled =
    !!input.checkOutPast &&
    tariffPayments === 0 &&
    (input.depositPaid === true || depositAmount > 0);

  if (historicalSettled) {
    return {
      paidAmount: grandTotal,
      balance: 0,
      status: "saldado_historico",
      label: BALANCE_LABELS.saldado_historico,
    };
  }

  // Piso: post check-in nunca mostramos MÁS deuda que la fórmula vieja
  // (hay reservas con anticipo cobrado que nunca se registró en el eje).
  const paidAmount = input.postCheckIn
    ? Math.max(tariffPayments, depositAmount)
    : depositAmount;

  const balance = grandTotal - paidAmount;

  // Negativo NO es "pagado de más": hay ≥3 causas raíz (doble depósito,
  // balance mal cargado, total subestimado). Va a revisión humana.
  const status: ReservationBalanceStatus =
    balance < 0
      ? "revisar"
      : balance === 0
        ? "saldado"
        : input.postCheckIn
          ? "no_cobrado_al_cierre"
          : "pendiente";

  return { paidAmount, balance, status, label: BALANCE_LABELS[status] };
}


/* ------------------------------------------------------------------ *
 * Recálculo de total desde la tarifa (cambio de alojamiento, upgrade,
 * edición de fechas, autogestión del huésped)
 * ------------------------------------------------------------------ */

export type TariffRecalcInput = {
  /** Nuevo subtotal de alojamiento */
  newSubtotal: number;
  /** Nueva tarifa de servicio */
  newServiceFee: number;
  /** Σ total_price de `reservation_additional_services` (precio PACTADO, nunca el catálogo) */
  servicesTotal: number;
  /** `reservations.discount_amount` */
  discountAmount?: number | null;
};

export type TariffRecalcResult = {
  /** Valor a escribir en `reservations.total` */
  storedTotal: number;
  /** Total real de la estadía (alojamiento + servicios − descuento) */
  grandTotal: number;
  /** Valor a escribir en `reservations.total_incluye_servicios` */
  totalIncluyeServicios: boolean;
};

/**
 * Única fórmula autorizada para recalcular el total cuando cambia la tarifa.
 *
 * Convención unificada: cuando hay servicios vendidos, `reservations.total`
 * SIEMPRE los incluye y la bandera queda en `true`. Así nunca se puede
 * persistir flag=true con un total que no los contiene (`flag_lying`) ni un
 * total que los omite (`total_missing_services`).
 */
export function recalcStoredTotalFromTariff(
  input: TariffRecalcInput
): TariffRecalcResult {
  const services = input.servicesTotal || 0;
  const discount = input.discountAmount || 0;
  const grandTotal = Math.max(
    0,
    (input.newSubtotal || 0) + (input.newServiceFee || 0) + services - discount
  );
  return {
    storedTotal: grandTotal,
    grandTotal,
    totalIncluyeServicios: services > 0,
  };
}
